import {
  Button,
  LinearProgress,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { canPlayVideoUrl, getYoutubePreviewThumbnail } from '@strength/domain';
import { MdPlayCircle } from '@teamworksdev/icons';
import React, { ChangeEvent, createRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { environment } from '../../../../environment';
import { useToast } from '../../../hooks/useToast';
import { VideoPreviewPlayer } from '../../multimedia/video-preview-player';
import { LabeledRadioChip } from '../labeled-radio-chip';
import { useUploadFile } from './file-upload-hook';

interface VideoUploaderProps {
  videoUrl: string | null;
  filePrefix: string;
  onChange: (url: string | null) => void;
  disabled?: boolean;
}

// for now external videos are only youtube
type VideoUrlType = 's3' | 'external';

export const VideoUploader = ({
  videoUrl,
  filePrefix,
  onChange,
  disabled = false,
}: VideoUploaderProps) => {
  const { toast } = useToast();
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const [inputUrl, setInputUrl] = useState<string>('');
  const [toggle, setToggle] = useState<VideoUrlType>(
    videoUrl ? getVideoUrlType(videoUrl) : 's3',
  );
  const [previewOpen, setPreviewOpen] = useState(false);

  /**
   * NOTE: this uploads to s3 everytime the video is changed
   */
  const [uploadFile, fileUploading] = useUploadFile({
    onComplete: (videoUrl: string): void => {
      if (videoUrl) {
        onChange(videoUrl);
      } else {
        onChange(null);
        setError(t('unableToUploadVideo'));
      }
    },
  });

  const showInvalidUrlToast = () =>
    toast({ title: t('error'), description: t('invalidUrl'), severity: 'error' });

  // for external url upload
  const convertTextToUrl = async (url: string): Promise<void> => {
    try {
      new URL(url);
    } catch (e) {
      showInvalidUrlToast();
      return;
    }
    if (!canPlayVideoUrl(url)) {
      showInvalidUrlToast();
      return;
    }
    onChange(url);
  };

  const handleDirectUpload = async (files: FileList | null) => {
    // if no file, clear the video
    if (!files) {
      onChange(null);
      return;
    }

    uploadFile(files, filePrefix, 'video');
  };

  return (
    <div>
      {videoUrl ? (
        <>
          <Typography
            variant="subtitle1"
            onClick={() => onChange(null)}
            className="hover:cursor-pointer text-blue-500 font-bold mb-1"
          >
            {t('replace')}
          </Typography>
          <VideoPreview
            image={
              toggle === 's3' ? (
                <video preload="metadata" className="h-10" src={videoUrl} />
              ) : (
                <img
                  src={getYoutubePreviewThumbnail(videoUrl)}
                  className="max-h-10"
                />
              )
            }
            onPreview={() => setPreviewOpen(true)}
          />
        </>
      ) : (
        <>
          <RadioGroup
            row
            name="videoUrlType"
            defaultValue="s3"
            value={toggle}
            onChange={(event) => setToggle(event.target.value as VideoUrlType)}
            sx={{ gap: 1, mb: 1 }}
          >
            <LabeledRadioChip
              key={'s3'}
              value={'s3'}
              label={t('fileUpload')}
              disabled={disabled}
            />
            <LabeledRadioChip
              key={'external'}
              value={'external'}
              label={t('youtubeLink')}
              disabled={disabled}
            />
          </RadioGroup>
          {toggle === 's3' && (
            <DirectUpload
              isUploading={fileUploading}
              error={error}
              handleDirectUpload={handleDirectUpload}
              disabled={disabled}
            />
          )}
          {toggle === 'external' && (
            <div className="flex gap-1">
              <TextField
                type="text"
                value={inputUrl}
                onChange={(e) => setInputUrl(e.target.value)}
                placeholder={t('addYoutubeLink')}
                className="w-full"
              />
              <Button
                disabled={!inputUrl}
                onClick={() => convertTextToUrl(inputUrl)}
              >
                {t('add')}
              </Button>
            </div>
          )}
        </>
      )}
      {previewOpen && videoUrl && (
        <VideoPreviewPlayer
          videoUrl={videoUrl}
          onClose={() => setPreviewOpen(false)}
        />
      )}
    </div>
  );
};

const DirectUpload = ({
  isUploading,
  error,
  handleDirectUpload,
  disabled,
}: {
  isUploading: boolean;
  error: string | null;
  handleDirectUpload: (files: FileList | null) => Promise<void>;
  disabled: boolean;
}) => {
  const { t } = useTranslation();
  const [inDropZone, setInDropZone] = useState(false);
  const inputRef = createRef<HTMLInputElement>();
  const openFileFinder = () => {
    if (inputRef && inputRef.current) {
      inputRef.current.click();
    }
  };

  const handleDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setInDropZone(true);
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
    setInDropZone(true);
  };

  const handleDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const files = event.dataTransfer.files;
    await handleDirectUpload(files);
    setInDropZone(false);
  };

  const handleDragExit = () => {
    setInDropZone(false);
  };

  return (
    <div
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onDragExit={handleDragExit}
      onDragLeave={handleDragExit}
    >
      {isUploading ? (
        <div>
          <Typography variant="subtitle1Medium">{t('loading')}</Typography>
          <LinearProgress className="mt-2" />
        </div>
      ) : error ? (
        <div>{t('failedToUploadVideo')}</div>
      ) : (
        <div
          className={
            inDropZone
              ? 'bg-blue-300 min-h-10 border border-slate-200 rounded-md py-4 content-center'
              : 'bg-slate-50 min-h-10 border border-slate-200 rounded-md py-4 content-center'
          }
        >
          <div className="flex justify-center text-xs text-slate-600">
            <div
              onClick={openFileFinder}
              className="hover:cursor-pointer text-blue-500 font-semibold mr-1"
            >
              {t('clickToUpload')}
            </div>
            {t('orDragAndDropFile')}
          </div>
          <div className="text-xs text-center text-slate-600">
            {t('videoFormatsAndSize')}
          </div>
        </div>
      )}
      <input
        type="file"
        disabled={disabled}
        style={{ display: 'none' }}
        ref={inputRef}
        accept="video/*"
        onChange={async (event: ChangeEvent<HTMLInputElement>) =>
          await handleDirectUpload(event.target.files)
        }
      />
    </div>
  );
};

const VideoPreview = ({
  image,
  onPreview,
}: {
  image: React.ReactNode;
  onPreview: () => void;
}) => {
  const { t } = useTranslation();
  return (
    <div className="flex gap-2 w-full border border-slate-200 rounded-md p-4 pb-3">
      <div
        className="relative hover:cursor-pointer"
        onClick={() => onPreview()}
      >
        {image}
        <MdPlayCircle
          color="white"
          className="z-2 absolute top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%]"
        />
      </div>
      <Typography variant="body2Medium" className="mt-3">
        {t('uploadedVideo')}
      </Typography>
    </div>
  );
};

const getVideoUrlType = (url: string): VideoUrlType => {
  return url.startsWith(environment.VITE_AWS_S3_URL) ||
    url.startsWith('http://localhost')
    ? 's3'
    : 'external';
};
